import { Controller } from "@hotwired/stimulus"
import { get, set as storeSet, subscribe } from "../lib/session_store"

export default class extends Controller {
  static targets = [
    "playBtn",
    "playIcon",
    "pauseIcon",
    "timeDisplay",
    "slider",
    "speedBtn",
  ]
  static values = {
    sessionId: String,
  }

  connect() {
    this.snapshots = []
    this.currentIndex = 0
    this.playing = false
    this.playbackSpeed = 1.0
    this.animationId = null
    this.lastFrameTime = 0
    this.accumulatedTime = 0
    this.loaded = false

    this._lapStarts = null

    this._unsubs = [
      subscribe("playbackMode", (mode) => {
        // If something else took over, stop our playback
        if (mode !== "replay" && this.playing) {
          this._stopPlayback()
        }
      }),
      subscribe("sessionTime", (sessionTime) => {
        // When we're NOT driving, react to external sessionTime changes (video sync, lap clicks)
        if (get("playbackMode") === "replay") return
        if (sessionTime != null) {
          this._seekToSessionTime(sessionTime)
        }
      }),
      subscribe("activeLapNumber", (lapNumber) => {
        this._seekToLapNumber(lapNumber)
      }),
    ]

    this._loadLapSessionTimes()
    this.loadSnapshots()
  }

  disconnect() {
    this.stop()
    this._unsubs?.forEach((fn) => fn())
  }

  async loadSnapshots() {
    try {
      const response = await fetch(
        `/sessions/${this.sessionIdValue}/snapshots.json`,
      )
      if (!response.ok) return

      this.snapshots = await response.json()
      this.loaded = this.snapshots.length > 0

      if (this.loaded && this.hasSliderTarget) {
        this.sliderTarget.max = this.snapshots.length - 1
        this.sliderTarget.value = 0
        this.updateTimeDisplay()
        this.emitSnapshot(0)
      }
    } catch (e) {
      // Silently fail — replay just won't be available
    }
  }

  play() {
    if (!this.loaded || this.snapshots.length === 0) return

    if (this.playing) {
      this.pause()
      return
    }

    this.playing = true
    storeSet("playbackMode", "replay")
    this.lastFrameTime = performance.now()
    this.accumulatedTime = 0
    this.updatePlayButton()
    this.tick()
  }

  pause() {
    this._stopPlayback()
    if (get("playbackMode") === "replay") {
      storeSet("playbackMode", "idle")
    }
  }

  // Internal stop without mode transition (used when another mode takes over)
  _stopPlayback() {
    this.playing = false
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    this.updatePlayButton()
  }

  stop() {
    this.pause()
    this.currentIndex = 0
    if (this.hasSliderTarget) {
      this.sliderTarget.value = 0
    }
    this.updateTimeDisplay()
  }

  tick() {
    if (!this.playing) return

    const now = performance.now()
    const dt = (now - this.lastFrameTime) / 1000
    this.lastFrameTime = now
    this.accumulatedTime += dt * this.playbackSpeed

    const currentSnapshot = this.snapshots[this.currentIndex]
    const nextIndex = this.currentIndex + 1

    if (nextIndex >= this.snapshots.length) {
      this.emitInterpolated(this.currentIndex, 0)
      this.pause()
      return
    }

    const nextSnapshot = this.snapshots[nextIndex]
    const interval = nextSnapshot.session_time - currentSnapshot.session_time

    // Advance index when we've passed the interval boundary
    if (interval > 0 && this.accumulatedTime >= interval) {
      this.accumulatedTime -= interval
      this.currentIndex = nextIndex

      if (this.hasSliderTarget) {
        this.sliderTarget.value = this.currentIndex
      }
    }

    // Calculate interpolation factor between current and next snapshot
    const cur = this.snapshots[this.currentIndex]
    const nxt = this.snapshots[this.currentIndex + 1]
    let t = 0
    if (nxt) {
      const span = nxt.session_time - cur.session_time
      t = span > 0 ? Math.min(this.accumulatedTime / span, 1) : 0
    }

    this.emitInterpolated(this.currentIndex, t)

    const interpolatedTime =
      cur.session_time + (nxt ? (nxt.session_time - cur.session_time) * t : 0)
    this.updateTimeDisplaySmooth(interpolatedTime)
    storeSet("sessionTime", interpolatedTime)

    this.animationId = requestAnimationFrame(() => this.tick())
  }

  seek() {
    if (!this.loaded || !this.hasSliderTarget) return
    const idx = parseInt(this.sliderTarget.value, 10)
    this.currentIndex = idx
    this.accumulatedTime = 0
    this.emitSnapshot(idx)
    this.updateTimeDisplay()

    // Slider drag takes over as replay mode
    storeSet("playbackMode", "replay")
    const snapshot = this.snapshots[idx]
    if (snapshot) {
      storeSet("sessionTime", snapshot.session_time)
    }
  }

  _seekToSessionTime(targetTime) {
    if (!this.loaded || this.snapshots.length === 0) return
    if (targetTime == null) return

    // Fast path: if target falls between current and next snapshot, skip binary search
    {
      const cur = this.snapshots[this.currentIndex]
      const nxt = this.snapshots[this.currentIndex + 1]
      if (
        cur &&
        nxt &&
        targetTime >= cur.session_time &&
        targetTime <= nxt.session_time
      ) {
        const span = nxt.session_time - cur.session_time
        const t = span > 0 ? (targetTime - cur.session_time) / span : 0
        this.accumulatedTime = targetTime - cur.session_time

        if (this.hasSliderTarget) {
          this.sliderTarget.value = this.currentIndex
        }

        this.emitInterpolated(this.currentIndex, t)
        this.updateTimeDisplaySmooth(targetTime)
        return
      }
    }

    // Binary search for the closest snapshot
    let lo = 0
    let hi = this.snapshots.length - 1
    while (lo < hi) {
      const mid = (lo + hi) >>> 1
      if (this.snapshots[mid].session_time < targetTime) {
        lo = mid + 1
      } else {
        hi = mid
      }
    }

    // Pick the closest of lo and lo-1
    if (lo > 0) {
      const prev = this.snapshots[lo - 1]
      const curr = this.snapshots[lo]
      if (
        Math.abs(prev.session_time - targetTime) <
        Math.abs(curr.session_time - targetTime)
      ) {
        lo = lo - 1
      }
    }

    this.currentIndex = lo
    this.accumulatedTime = 0

    if (this.hasSliderTarget) {
      this.sliderTarget.value = this.currentIndex
    }

    // Compute interpolation toward next snapshot
    const cur = this.snapshots[this.currentIndex]
    const nxt = this.snapshots[this.currentIndex + 1]
    let t = 0
    if (nxt) {
      const span = nxt.session_time - cur.session_time
      if (span > 0) {
        t = Math.max(0, Math.min((targetTime - cur.session_time) / span, 1))
      }
    }

    this.emitInterpolated(this.currentIndex, t)
    this.updateTimeDisplaySmooth(targetTime)
  }

  // Legacy Stimulus action interface (used by seekToSessionTime events)
  seekToSessionTime(event) {
    const targetTime = event.detail?.sessionTime
    if (targetTime == null) return
    this._seekToSessionTime(targetTime)
  }

  async _loadLapSessionTimes() {
    try {
      const resp = await fetch(
        `/sessions/${this.sessionIdValue}/lap_session_times.json`,
      )
      if (!resp.ok) return
      const data = await resp.json()
      this._lapStarts = data.lap_starts || {}
    } catch (_) {
      // silently fail
    }
  }

  _seekToLapNumber(lapNumber) {
    if (!this.loaded || !this._lapStarts) return

    const sessionTime = this._lapStarts[String(lapNumber)]
    if (sessionTime == null) return

    this._seekToSessionTime(sessionTime)
    storeSet("sessionTime", sessionTime)
  }

  setSpeed(event) {
    const speed = parseFloat(event.currentTarget.dataset.speed)
    if (!isNaN(speed)) {
      this.playbackSpeed = speed
      this.updateSpeedButtons()
    }
  }

  // Emit an interpolated frame between snapshot[index] and snapshot[index+1]
  emitInterpolated(index, t) {
    const current = this.snapshots[index]
    if (!current) return

    const next = this.snapshots[index + 1]
    if (!next || t === 0) {
      const { metadata, ...rest } = current
      this.dispatch("update", {
        detail: { type: "race_state", ...rest, ...(metadata || {}) },
      })
      return
    }

    const { metadata, ...currentRest } = current
    const interpolated = {
      ...currentRest,
      ...(metadata || {}),
      session_time:
        current.session_time + (next.session_time - current.session_time) * t,
      cars: this.interpolateCars(current.cars, next.cars, t),
    }

    this.dispatch("update", { detail: { type: "race_state", ...interpolated } })
  }

  interpolateCars(currentCars, nextCars, t) {
    if (!currentCars) return currentCars
    if (!nextCars) return currentCars

    const nextByIndex = new Map()
    for (const car of nextCars) {
      nextByIndex.set(car.car_index, car)
    }

    return currentCars.map((car) => {
      const next = nextByIndex.get(car.car_index)
      if (!next) return car

      return {
        ...car,
        world_position_x:
          car.world_position_x +
          (next.world_position_x - car.world_position_x) * t,
        world_position_z:
          car.world_position_z +
          (next.world_position_z - car.world_position_z) * t,
        speed_kmh: Math.round(
          car.speed_kmh + (next.speed_kmh - car.speed_kmh) * t,
        ),
      }
    })
  }

  emitSnapshot(index) {
    const snapshot = this.snapshots[index]
    if (!snapshot) return

    const { metadata, ...rest } = snapshot
    this.dispatch("update", {
      detail: {
        type: "race_state",
        ...rest,
        ...(metadata || {}),
      },
    })
  }

  updatePlayButton() {
    if (!this.hasPlayIconTarget || !this.hasPauseIconTarget) return
    this.playIconTarget.classList.toggle("hidden", this.playing)
    this.pauseIconTarget.classList.toggle("hidden", !this.playing)
  }

  updateTimeDisplay() {
    if (!this.hasTimeDisplayTarget || !this.loaded) return
    const snapshot = this.snapshots[this.currentIndex]
    if (!snapshot) return
    this.updateTimeDisplaySmooth(snapshot.session_time)
  }

  updateTimeDisplaySmooth(sessionTime) {
    if (!this.hasTimeDisplayTarget) return
    const totalSeconds = Math.floor(sessionTime)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60
    this.timeDisplayTarget.textContent = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
  }

  updateSpeedButtons() {
    if (!this.hasSpeedBtnTarget) return
    this.speedBtnTargets.forEach((btn) => {
      const speed = parseFloat(btn.dataset.speed)
      if (speed === this.playbackSpeed) {
        btn.classList.add("bg-vivid-purple", "text-white")
        btn.classList.remove("text-white/60")
      } else {
        btn.classList.remove("bg-vivid-purple", "text-white")
        btn.classList.add("text-white/60")
      }
    })
  }
}
